import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { PaginationQueryDto } from '../data-transfer/entities/pagination-query-dto';
import { PaginationResponseDto } from '../data-transfer/entities/pagination-response-dto';
import { PaginationFilterDto } from '../data-transfer/entities/pagination-filter-dto';

export type PaginationEndpoint<T> = (query: PaginationQueryDto) => Observable<PaginationResponseDto<T>>;

export class PaginationDataSource<T> extends DataSource<T> {
  private loading = new BehaviorSubject<boolean>(true);
  private paginationQuery: PaginationQueryDto;
  //keep track of the current request subscription to allow cancellation before making a new request
  private currentRequestSubscription?: Subscription;

  private pageSubject!: BehaviorSubject<PaginationResponseDto<T>>;

  private filteredColumnsSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  constructor(private endpoint: PaginationEndpoint<T>) {
    super();

    this.paginationQuery = {
      pageIndex: 0,
      pageSize: 1,
      sortByField: undefined,
      sortAscending: false,
      filters: [],
    };

    this.pageSubject = new BehaviorSubject<PaginationResponseDto<T>>({
      pageIndex: 0,
      totalPages: 1,
      totalItemCount: 0,
      items: [],
      allIds: [],
    });
  }

  connect(collectionViewer: CollectionViewer): Observable<readonly T[]> {
    return this.pageSubject.pipe(map(x => x.items));
  }

  disconnect(collectionViewer: CollectionViewer): void {}

  get data(): T[] {
    return this.pageSubject.value.items;
  }

  get page(): Observable<PaginationResponseDto<T>> {
    return this.pageSubject;
  }

  get filteredColumns(): BehaviorSubject<string[]> {
    return this.filteredColumnsSubject;
  }

  get isLoading(): Observable<boolean> {
    return this.loading;
  }

  get filters() {
    return this.paginationQuery.filters;
  }

  get sortByField(): string | undefined {
    return this.paginationQuery.sortByField;
  }

  get sortAscending(): boolean {
    return this.paginationQuery.sortAscending;
  }

  addFilter(fieldName: string, value: any) {
    this.addFilterOptions(fieldName, [value]);
  }

  addFilterOptions(fieldName: string, options: any[], mode: 'OR' | 'AND' = 'OR') {
    this.paginationQuery.filters = this.paginationQuery.filters.filter(x => x.fieldName !== fieldName);

    const values = options.filter(value => value !== undefined).map(value => (value === null ? null : value.toString()));

    if (values.length > 0) {
      if (mode === 'AND') {
        for (const value of values) {
          this.paginationQuery.filters.push({ fieldName, values: [value] });
        }
      } else {
        this.paginationQuery.filters.push({ fieldName, values });
      }
    }

    this.paginationQuery.pageIndex = 0;
    this.refresh();
  }

  sort(fieldName: string, ascending: boolean) {
    this.paginationQuery.sortByField = fieldName;
    this.paginationQuery.sortAscending = ascending;

    this.refresh();
  }

  removeSorting() {
    this.paginationQuery.sortByField = undefined;
    this.refresh();
  }

  refresh() {
    this.loading.next(true);

    this.currentRequestSubscription?.unsubscribe();
    this.currentRequestSubscription = this.endpoint(this.paginationQuery).subscribe(result => {
      this.pageSubject.next(result);
      this.filteredColumns.next(this.paginationQuery.filters.map(x => x.fieldName));
      this.loading.next(false);
    });
  }

  clear() {
    this.paginationQuery.filters = [];
    this.pageSubject.next({
      pageIndex: 0,
      totalPages: 1,
      totalItemCount: 0,
      items: [],
      allIds: [],
    });
    this.loading.next(false);
  }

  fetchData(pageIndex: number, pageSize: number, filters: PaginationFilterDto[] | undefined = undefined) {
    this.paginationQuery.pageIndex = pageIndex;
    this.paginationQuery.pageSize = pageSize;

    if (filters) {
      this.paginationQuery.filters = filters;
    }

    this.refresh();
  }

  setLoading(value: boolean): void {
    this.loading.next(value);
  }
}
